import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Clock,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  RefreshCw,
  TrendingUp,
  Sparkles,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react'
import api from '../../services/api'
import toast from 'react-hot-toast'

export default function DailyAcademyBriefing() {
  const [briefing, setBriefing] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)

  const fetchBriefing = async (isManual = false) => {
    if (isManual) setRefreshing(true)
    try {
      const res = await api.get('/intelligence/daily')
      if (res.data?.briefing) {
        setBriefing(res.data.briefing)
        setError(null)
      } else {
        setError('Briefing data format unexpected.')
      }
    } catch (err) {
      console.error('Failed to load Daily Academy Briefing:', err)
      const status = err.response?.status
      if (status === 403) {
        setError('Access denied: Staff role required.')
      } else {
        setError(err.response?.data?.error || 'Failed to load daily briefing.')
      }
    } finally {
      setLoading(false)
      if (isManual) setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchBriefing()
  }, [])

  if (loading) {
    return (
      <div className="py-8 border-b border-gray-200 dark:border-gray-800 animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-none w-1/4"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded-none w-1/2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-none w-3/4"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-6 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400 font-sans">
            Daily briefing unavailable: {error}
          </p>
          <button
            onClick={() => fetchBriefing(true)}
            className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!briefing) return null

  const attentionItems = briefing.attention_items || []
  const highItems = attentionItems.filter(i => i.severity === 'HIGH')
  const mediumItems = attentionItems.filter(i => i.severity === 'MEDIUM')
  const otherItems = attentionItems.filter(i => i.severity !== 'HIGH' && i.severity !== 'MEDIUM')

  const formattedDate = briefing.period?.date
    ? new Date(briefing.period.date + 'T00:00:00Z').toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : 'Today'

  const getDomainRoute = (domain, entityType) => {
    switch (domain) {
      case 'ADMISSIONS':
        return '/admin/admissions'
      case 'STUDENTS':
        return '/admin/students'
      case 'ACADEMICS':
      case 'TRAINERS':
        return '/admin/courses'
      case 'PROJECTS':
        return '/admin/project-lab'
      case 'INTAKES':
        return '/admin/intakes'
      case 'OPERATIONS':
      default:
        return '/admin/ai-admin/action-center'
    }
  }

  return (
    <section className="pt-2 pb-10 border-b border-gray-200 dark:border-gray-800 font-sans">
      {/* ─── Header & Metadata ─── */}
      <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-3">
        <div>
          <span className="text-[11px] uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">
            TODAY &bull; {formattedDate}
          </span>
          <h2
            className="text-xl sm:text-2xl text-gray-900 dark:text-white mt-1 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
          >
            Academy Briefing
          </h2>
        </div>

        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800/60">
            {briefing.provenance?.ai_enhanced ? (
              <>
                <Sparkles size={11} className="text-amber-500" />
                <span>Grounded AI Briefing</span>
              </>
            ) : (
              <>
                <CheckCircle2 size={11} className="text-green-500" />
                <span>Deterministic Live Briefing</span>
              </>
            )}
          </span>
          <button
            onClick={() => fetchBriefing(true)}
            disabled={refreshing}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-1"
            title="Refresh briefing from live data"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* ─── Grounded Summary ─── */}
      <p className="text-sm sm:text-[15px] text-gray-700 dark:text-gray-300 leading-relaxed max-w-3xl mb-8">
        {briefing.summary}
      </p>

      {/* ─── Editorial Grid: What Changed & Attention ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Left Column: What Changed */}
        <div className="lg:col-span-5 space-y-4">
          <div className="border-b border-gray-200 dark:border-gray-800 pb-2">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-gray-900 dark:text-white">
              WHAT CHANGED
            </h3>
          </div>

          <div className="space-y-3 text-sm">
            {briefing.what_changed && briefing.what_changed.length > 0 ? (
              briefing.what_changed.map((change, idx) => (
                <div key={idx} className="flex items-start gap-2 text-gray-600 dark:text-gray-300">
                  <span className="text-gray-400 dark:text-gray-600 mt-0.5">&mdash;</span>
                  <span className="leading-snug">{change}</span>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                No significant operational movements recorded since previous cycle.
              </p>
            )}
          </div>

          {/* Next Steps preview */}
          {briefing.recommended_next_steps && briefing.recommended_next_steps.length > 0 && (
            <div className="pt-6 border-t border-gray-100 dark:border-gray-800/60 mt-6 space-y-3">
              <h4 className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                RECOMMENDED INVESTIGATION
              </h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                {briefing.recommended_next_steps.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-primary-600 dark:text-primary-400 font-semibold">{idx + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right Column: Attention Items */}
        <div className="lg:col-span-7 space-y-4">
          <div className="border-b border-gray-200 dark:border-gray-800 pb-2 flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-gray-900 dark:text-white">
              ATTENTION ({attentionItems.length})
            </h3>
            {highItems.length > 0 && (
              <span className="text-[11px] text-red-600 dark:text-red-400 font-medium">
                {highItems.length} High Priority
              </span>
            )}
          </div>

          {attentionItems.length === 0 ? (
            <div className="py-6 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <CheckCircle2 size={18} className="text-green-500 shrink-0" />
              <span>All student cohorts, admissions queues, and project labs are within normal operational thresholds.</span>
            </div>
          ) : (
            <div className="space-y-4">
              {attentionItems.map((item) => {
                const isHigh = item.severity === 'HIGH'
                const isMed = item.severity === 'MEDIUM'
                const route = getDomainRoute(item.domain, item.entity_type)

                return (
                  <div
                    key={item.signal_id}
                    className="p-3.5 rounded-none border border-gray-200/80 dark:border-gray-800/80 bg-gray-50/50 dark:bg-gray-900/30 space-y-2 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-none ${
                            isHigh
                              ? 'bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/20'
                              : isMed
                              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20'
                              : 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20'
                          }`}
                        >
                          {item.severity}
                        </span>
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                          {item.domain} &bull; {item.entity_type} {item.entity_name ? `(${item.entity_name})` : `#${item.entity_id}`}
                        </span>
                      </div>

                      <Link
                        to={route}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 font-medium group"
                      >
                        <span>Inspect</span>
                        <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    </div>

                    <p className="text-xs sm:text-sm text-gray-800 dark:text-gray-200 leading-snug">
                      {item.reason}
                    </p>

                    {item.ai_explanation && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                        &ldquo;{item.ai_explanation}&rdquo;
                      </p>
                    )}

                    {/* Evidence + provenance (server-owned) */}
                    {((item.evidence && item.evidence.length > 0) || item.source) && (
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-1">
                        {(item.evidence || []).map((ev, idx) => (
                          <span
                            key={idx}
                            className="text-[11px] text-gray-600 dark:text-gray-400 font-mono"
                          >
                            <strong className="font-semibold text-gray-700 dark:text-gray-300">{ev.label}:</strong>{' '}
                            {String(ev.value)}
                            {ev.comparison ? (
                              <span className="text-gray-400 dark:text-gray-500"> ({ev.comparison})</span>
                            ) : null}
                          </span>
                        ))}
                        {item.source && (
                          <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-mono">
                            source: {item.source}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
